// Product page (<product-page>). Two jobs: the gallery, and switching
// variants without a full page load.
//
// The picker never combines options itself. Liquid already put the target
// variant on every value through `product_option_value.variant`, so a
// click only has to read data-variant and ask the server for that URL.
//
// Same swap discipline as the results component: narrow regions, and the
// control being used is inside one of them, so focus is restored by name
// and value afterwards.
//
// Adding to cart is NOT here. That is the cart's job and lives in
// cart.js, which owns the drawer, the badge and the section render.

// A barra fixa repete título e preço, então entra na mesma troca: sem
// isso o preço dela ficaria no da variante anterior.
const REGIONS = ['[data-badge]', '[data-sku]', '[data-price]', '[data-variants]', '[data-buy]', '[data-sticky]'];

class ProductPage extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.rail = this.querySelector('[data-media-rail]');
    if (!this.sectionId) return;

    this.listeners = new AbortController();
    const { signal } = this.listeners;

    this.addEventListener('change', this.handleChange, { signal });
    this.addEventListener('click', this.handleClick, { signal });
    window.addEventListener('popstate', this.handlePopstate, { signal });

    this.setupVariantImages();

    if (this.rail) {
      this.rail.addEventListener('scroll', this.syncThumbs, { passive: true, signal });
      // Once at start too, so the progress line has its width before any swipe.
      this.syncThumbs();
    }
  }

  disconnectedCallback() {
    this.listeners?.abort();
    this.pending?.abort();
  }

  handleChange = (event) => {
    const input = event.target.closest('.variant-picker__input');
    if (!input) return;
    const id = input.dataset.variant;
    // A value with no variant is a combination the product does not sell.
    if (!id) return;
    this.selectVariant(id);
  };

  // One route for every variant change, whoever asks for it: the picker
  // in the column or the list in the sticky bar.
  selectVariant(id) {
    const url = `${this.dataset.productUrl}?variant=${id}`;
    // Inside the quick view the address bar belongs to the listing
    // behind it, not to the product being peeked at.
    if (!this.hasAttribute('data-static-url')) history.pushState({}, '', url);
    this.render(url);
  }

  handleClick = (event) => {
    const step = event.target.closest('[data-step]');
    if (step) return this.stepQuantity(Number(step.dataset.step));

    const thumb = event.target.closest('[data-thumb]');
    if (thumb) this.showMedia(thumb.dataset.thumb);
  };

  handlePopstate = () => {
    if (this.hasAttribute('data-static-url')) return;
    this.render(window.location.href);
  };

  stepQuantity(by) {
    const field = this.querySelector('.product__quantity-input');
    if (!field) return;
    const next = Math.max(Number(field.min || 1), (Number(field.value) || 1) + by);
    if (next === Number(field.value)) return;
    field.value = next;
  }

  // Fotos por variante (formato do app FoxKit, lido também pelo Minimog).
  // Guarda os elementos originais de cada lista e, a cada troca, repõe só
  // os da variante, na ordem do app. Tirar do DOM em vez de esconder deixa
  // setas, contador do zoom e miniaturas contando certo sem saber disso.
  setupVariantImages() {
    const source = this.querySelector('[data-variant-images]');
    if (!source) return;
    let data = null;
    try {
      data = JSON.parse(source.textContent);
    } catch {
      return;
    }
    if (!data?.enable || !Array.isArray(data.mapping)) return;
    this.variantImages = data;

    const key = (el) => el.dataset.mediaId || el.dataset.thumb || el.dataset.lightboxMedia;
    this.mediaLists = [this.rail, this.querySelector('.product__thumbs'), document.querySelector('.product__lightbox-rail')]
      .filter(Boolean)
      .map((container) => {
        const items = Array.from(container.children).filter(key);
        items.forEach((el) => el.removeAttribute('hidden'));
        return { container, items, key };
      });

    this.applyVariantImages(this.querySelector('[data-variant-select] option:checked')?.value, false);
  }

  applyVariantImages(variantId, scroll = true) {
    if (!this.variantImages || !variantId) return;
    const group = this.variantImages.mapping.find((entry) => String(entry.id) === String(variantId));
    const ids = group?.media?.length ? group.media.map(String) : null;

    this.mediaLists.forEach(({ container, items, key }) => {
      let list = items;
      if (ids) {
        const chosen = ids.map((id) => items.find((el) => key(el) === id)).filter(Boolean);
        // Vídeo e modelo 3D não entram no mapa do app: ficam sempre, no fim.
        const others = items.filter((el) => el.dataset.mediaType !== 'image' && !ids.includes(key(el)));
        if (chosen.length) list = [...chosen, ...others];
      }
      container.replaceChildren(...list);
    });

    const total = document.querySelector('.product__lightbox [data-total]');
    const lightboxList = this.mediaLists.find(({ container }) => container.classList.contains('product__lightbox-rail'));
    if (total && lightboxList) total.textContent = String(lightboxList.container.children.length);

    if (this.rail) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rail.scrollTo({ left: 0, behavior: scroll && !reduce ? 'smooth' : 'instant' });
      this.syncThumbs();
    }
  }

  // Scrolls rather than hides: the rail is the same element on both
  // breakpoints, so a thumbnail click and a swipe end in the same state.
  showMedia(mediaId) {
    const item = this.querySelector(`[data-media-id="${CSS.escape(String(mediaId))}"]`);
    if (!item || !this.rail) return;
    // Reduced motion jumps to the photo instead of gliding; 'instant',
    // because 'auto' would follow a CSS smooth (see carousel.js).
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.rail.scrollTo({ left: item.offsetLeft - this.rail.offsetLeft, behavior: reduce ? 'instant' : 'smooth' });
  }

  // Which photo is centred right now decides which thumbnail is marked.
  // Reading scroll position beats tracking clicks: a swipe changes the
  // photo without any click to listen to.
  syncThumbs = () => {
    if (!this.rail) return;
    // Progress line: how much of the rail is on screen, and how far in.
    const overflow = this.rail.scrollWidth - this.rail.clientWidth;
    this.style.setProperty('--gallery-window', String(this.rail.clientWidth / this.rail.scrollWidth));
    this.style.setProperty('--gallery-progress', String(overflow > 0 ? this.rail.scrollLeft / overflow : 0));
    const middle = this.rail.scrollLeft + this.rail.clientWidth / 2;
    let current = null;
    this.querySelectorAll('[data-media-id]').forEach((item) => {
      const start = item.offsetLeft - this.rail.offsetLeft;
      if (start <= middle && start + item.clientWidth > middle) current = item.dataset.mediaId;
    });
    if (!current) return;
    this.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.toggleAttribute('aria-current', thumb.dataset.thumb === current);
      if (thumb.dataset.thumb === current) thumb.setAttribute('aria-current', 'true');
    });
  };

  async render(url) {
    this.pending?.abort();
    const request = new AbortController();
    this.pending = request;
    this.setAttribute('loading', '');

    const active = document.activeElement;
    const key = active?.name && active.value
      ? `input[name="${CSS.escape(active.name)}"][value="${CSS.escape(active.value)}"]`
      : null;

    try {
      const target = new URL(url, window.location.origin);
      target.searchParams.set('sections', this.sectionId);

      const response = await fetch(target, { signal: request.signal });
      const data = await response.json();
      const html = data[this.sectionId];
      // A section that fails to render comes back as null inside a 200.
      if (!html) throw new Error('section did not render');

      const fresh = new DOMParser().parseFromString(html, 'text/html');
      REGIONS.forEach((selector) => {
        const next = fresh.querySelector(selector);
        const current = this.querySelector(selector);
        if (next && current) current.replaceWith(next);
      });

      if (key) this.querySelector(key)?.focus({ preventScroll: true });

      // The gallery follows the chosen variant: the select that came back
      // in the swapped region carries the media of the new variant. With
      // photos grouped per variant the gallery is rebuilt instead, and the
      // first photo of the new colour is the one to land on.
      const option = this.querySelector('[data-variant-select] option:checked');
      if (this.variantImages) {
        this.applyVariantImages(option?.value);
      } else if (option?.dataset.media) {
        this.showMedia(option.dataset.media);
      }
    } catch (error) {
      // An aborted request is the next one taking over, not a failure.
      if (error.name !== 'AbortError') window.location.assign(url);
    } finally {
      if (this.pending === request) this.removeAttribute('loading');
    }
  }
}

if (!customElements.get('product-page')) {
  customElements.define('product-page', ProductPage);
}
